/**
 * Token-styled select for the Growth module — replaces native <select>
 * (browser default popovers don't match the app). Compact input-height
 * trigger + themed popover: accent-highlighted selection, optional option
 * groups (career tracks), and a filter box on long lists (View-as).
 *
 * The popover renders through a portal with fixed positioning anchored to
 * the trigger, so it can't be clipped by overflow-hidden containers
 * (SettingsCard, modals) or shift the surrounding layout. It closes on
 * scroll/resize rather than tracking the anchor.
 *
 * Growth-local rather than reusing ui/StyledSelect: that component predates
 * the design tokens (slate/zinc + dark: variants) and has no group support.
 */
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Search } from 'lucide-react';

const POPOVER_MAX_HEIGHT = 300; // px — matches max-h list + search row

export function PortalSelect({
  value,
  onChange,
  options = null, // flat: [{ value, label }]
  groups = null, // grouped: [{ label, options: [{ value, label }] }]
  placeholder = 'Select…',
  disabled = false,
  className = '', // trigger width etc., e.g. "w-44"
  align = 'left', // popover edge: 'left' | 'right'
  ariaLabel = null,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const flatOptions = groups ? groups.flatMap((g) => g.options) : options || [];
  const selected = flatOptions.find((o) => o.value === value);
  const searchable = flatOptions.length > 10;
  const q = query.trim().toLowerCase();

  const close = () => { setOpen(false); setQuery(''); };

  const openPopover = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Flip upward when there's no room below.
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < POPOVER_MAX_HEIGHT && rect.top > spaceBelow;
    setPosition({
      top: openUp ? undefined : rect.bottom + 6,
      bottom: openUp ? window.innerHeight - rect.top + 6 : undefined,
      left: align === 'right' ? undefined : rect.left,
      right: align === 'right' ? window.innerWidth - rect.right : undefined,
      minWidth: rect.width,
    });
    setOpen(true);
  };

  // Escape closes; any scroll or resize closes too (fixed positioning would
  // otherwise drift away from the trigger).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    const onResize = () => close();
    // Capture-phase so scrolls of any ancestor container close the popover,
    // but scrolling the option list itself must not.
    const onScroll = (e) => {
      if (popoverRef.current?.contains(e.target)) return;
      close();
    };
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const filterOptions = (list) => (q ? list.filter((o) => o.label.toLowerCase().includes(q)) : list);

  const renderOption = (o) => {
    const isSel = o.value === value;
    return (
      <button
        key={o.value || '__empty'}
        type="button"
        role="option"
        aria-selected={isSel}
        onClick={() => { onChange(o.value); close(); }}
        className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg text-[12.5px] text-left transition-colors
          ${isSel ? 'bg-accent-wash text-accent font-semibold' : 'text-ink-2 hover:bg-surface-hover'}`}
      >
        <span className="truncate">{o.label}</span>
        {isSel && <Check size={13} className="flex-none" />}
      </button>
    );
  };

  const visibleGroups = groups
    ? groups.map((g) => ({ ...g, options: filterOptions(g.options) })).filter((g) => g.options.length > 0)
    : null;
  const visibleFlat = groups ? null : filterOptions(options || []).slice(0, 150);
  const empty = groups ? visibleGroups.length === 0 : visibleFlat.length === 0;

  return (
    <div className={`relative ${disabled ? 'opacity-60 pointer-events-none' : ''} ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (open ? close() : openPopover())}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel || undefined}
        className={`w-full h-8 flex items-center justify-between gap-1.5 rounded-ctl border px-2.5 text-[12px] transition-colors
          ${open ? 'border-accent bg-surface' : 'border-line bg-surface hover:bg-surface-hover'}`}
      >
        <span className={`truncate ${selected ? 'text-ink-2 font-semibold' : 'text-ink-4'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown size={13} className={`flex-none text-ink-4 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && position &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[120]" onClick={close} />
            <div
              ref={popoverRef}
              role="listbox"
              style={{
                position: 'fixed',
                top: position.top,
                bottom: position.bottom,
                left: position.left,
                right: position.right,
                minWidth: position.minWidth,
              }}
              className="z-[125] w-max max-w-[280px] rounded-card border border-line bg-surface shadow-e3 overflow-hidden"
            >
              {searchable && (
                <div className="relative p-1.5 border-b border-line">
                  <Search size={12} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-4 pointer-events-none" />
                  <input
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Type to filter…"
                    className="w-full h-7 rounded-lg pl-6 pr-2 text-[12px] bg-surface-2 border border-line text-ink placeholder-ink-4 outline-none focus:border-accent"
                  />
                </div>
              )}
              <div className="max-h-56 overflow-y-auto p-1">
                {visibleGroups &&
                  visibleGroups.map((g) => (
                    <div key={g.label}>
                      <p className="px-2.5 pt-2 pb-1 text-[10px] font-bold text-ink-4 uppercase tracking-widest">{g.label}</p>
                      {g.options.map(renderOption)}
                    </div>
                  ))}
                {visibleFlat && visibleFlat.map(renderOption)}
                {empty && <p className="text-[11.5px] text-center py-3 text-ink-4">No matches</p>}
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
}
